import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { DatasetFilterComponent } from '@components/dataset/list/filter/dialog.component';
import { AuthService, DatasetService, NotificationService } from '@services';
import { MatListModule } from '@angular/material/list';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { ListComponent } from '@components/list/list.component';
import { HttpParams, httpResource } from '@angular/common/http';
import { environment } from '@environment';

import {
  Dataset,
  GeoJSONToWKT,
  ItemListResponse,
  Label,
  Person,
  WKTToGeoJSON,
} from '@models';

@Component({
  selector: 'app-dataset-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    LeafletGroupComponent,
    ListComponent,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTooltipModule,
    RouterModule,
  ],
})
export class DatasetListComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dialog = inject(MatDialog);
  router = inject(Router);
  route = inject(ActivatedRoute);
  authService = inject(AuthService);
  datasetService = inject(DatasetService);
  notificationService = inject(NotificationService);

  q = signal<string>(this.route.snapshot.queryParams['q']);
  labelId = signal<string | undefined>(
    this.route.snapshot.queryParams['labelId'],
  );
  mapExpanded = signal<boolean>(false);
  location = signal<string | undefined>(
    this.route.snapshot.queryParams['location'],
  );
  geojson = computed(() => WKTToGeoJSON(this.location()));
  type = signal<string | undefined>(this.route.snapshot.queryParams['type']);
  role = signal<'owner' | 'contributor' | 'reader' | undefined>(this.route.snapshot.queryParams['permission'])
  from = signal<string | undefined>(this.route.snapshot.queryParams['from']);
  until = signal<string | undefined>(this.route.snapshot.queryParams['until']);

  parseDateFilter = (value?: string) => {
    if (!value) {
      return null;
    }

    let field = ["created:", "modified:", "published:", "timeframe:"].find(field => value.startsWith(field));
    const date = field ? value.substring(field.length) : value;
    field = field?.substring(0, field.length - 1)

    return { field: field || "modified", date };
  };
  dateField = computed(() => this.parseDateFilter(this.from() || this.until())?.field);
  dateFrom = computed(() => this.parseDateFilter(this.from())?.date);
  dateUntil = computed(() => this.parseDateFilter(this.until())?.date);

  order = signal<string | undefined>(this.route.snapshot.queryParams['order']);

  page = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  pageSize = signal(
    parseInt(this.route.snapshot.queryParams['pageSize']) || 10,
  );
  count = signal(0);

  datasets = httpResource<ItemListResponse<Dataset>>(() => {
    const count = untracked(() => this.count() === 0 || this.page() === 0);
    const params = {
      skip: this.page() * this.pageSize(),
      take: this.pageSize(),
      count: count,
      order: this.order() || 'created:desc',
      q: this.q(),
      labelId: this.labelId(),
      location: this.location(),
      type: this.type(),
      from: this.from(),
      until: this.until(),
      mayRead: this.role() === 'reader',
      mayUpdate: this.role() === 'contributor',
      mayDelete: this.role() === 'owner',
    };

    if (params.until && this.dateUntil()) {
      const dayAfterUntil = new Date(this.dateUntil()!).getTime() + 86400000;
      params.until = (new Date(dayAfterUntil)).toISOString();

      if (this.dateField()) {
        params.until = `${this.dateField()}:${params.until}`
      }
    }

    let httpParams = new HttpParams();
    for (const k in params) {
      const v = params[k as keyof typeof params];
      if (v) {
        httpParams = httpParams.set(k, v);
      }
    }

    return `${environment.kinkoHost}/dataset/?${httpParams.toString()}`;
  });

  label = httpResource<Label>(() => {
    const id = this.labelId();
    if (!id) {
      return undefined;
    }
    return `${environment.kinkoHost}/label/${id}`;
  });

  constructor() {
    this.route.queryParams.subscribe((params: Params) => {
      this.page.set(params['page'] || 0);
      this.pageSize.set(params['pageSize'] || 10);

      this.q.set(params['q']);
      this.labelId.set(params['labelId']);
      this.location.set(params['location']);
      this.type.set(params['type']);
      this.role.set(params['role']);
      this.from.set(params['from']);
      this.until.set(params['until']);

      this.order.set(params['order']);

      if (this.location()) {
        this.mapExpanded.set(true);
      } else {
        this.mapExpanded.set(false);
      }
    });

    effect(() => {
      if (!this.datasets.hasValue() || !this.datasets.value().count) {
        return;
      }

      this.count.set(this.datasets.value().count);
    });
  }

  onPageEvent(event: PageEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        page: event.pageIndex || undefined,
        pageSize: event.pageSize === 10 ? undefined : event.pageSize,
      },
      replaceUrl: true,
    });
  }

  advancedSearch() {
    this.dialog
      .open(DatasetFilterComponent, {
        data: {
          search: this.q(),
          role: this.role(),
          status: this.type(),
          label: this.label.hasValue() ? this.label.value() : undefined,
          geojson: WKTToGeoJSON(this.location()),
          order: this.order(),
          dateField: this.dateField(),
          dateFrom: this.dateFrom(),
          dateUntil: this.dateUntil(),
        },
        minWidth: '360px',
        maxWidth: '960px',
      })
      .afterClosed()
      .subscribe(
        (
          data?: {
            search?: string;
            role?: string;
            status?: 'draft' | 'public';
            label?: Label;
            geojson?: GeoJSON.FeatureCollection;
            order?: string;
            dateField: 'created' | 'modified' | 'published' | 'timeframe';
            dateFrom?: Date;
            dateUntil?: Date;
          },
        ) => {
          if (!data) {
            return;
          }

          let location: string | undefined = undefined;
          if (data.geojson?.features?.length) {
            location = GeoJSONToWKT(data.geojson);
          }

          let from: string | undefined = undefined;
          if (data.dateFrom) {
            from = `${data.dateField}:${data.dateFrom.toISOString()}`
          }

          let until: string | undefined = undefined;
          if (data.dateUntil) {
            until = `${data.dateField}:${data.dateUntil.toISOString()}`
          }

          const params = {
            q: data.search,
            labelId: data.label?.id,
            location: location,
            page: undefined,
            role: data.role,
            type: data.status,
            order: data.order,
            from: from,
            until: until,
          };

          this.router.navigate([], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
            queryParams: params,
            replaceUrl: true,
          });
        },
      );
  }

  onFilterChange(key: string | string[], value: string | undefined) {
    const queryParams: Record<string, string | undefined> = {
      q: this.q(),
      labelId: this.labelId(),
      location: this.location(),
      type: this.type() || undefined,
      page: undefined,
      order: this.order(),
      role: this.role(),
      from: this.from(),
      until: this.until(),
    };

    if (Array.isArray(key)) {
      for (const k of key) {
        queryParams[k] = value;
      }
    } else {
      queryParams[key] = value;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  authors(dataset: Dataset): string {
    return DatasetListComponent.authors(dataset);
  }

  static authors(dataset: Dataset): string {
    let authors = dataset.content.people
      ?.filter((p) => p.roles.includes('author'))
      .map((p: Person) => {
        const initials = p.firstName
          .split(' ')
          .filter((name) => name)
          .map((name) => name[0] + '.')
          .join(' ');

        return `${p.lastName}, ${initials}`;
      });

    if (!authors?.length) {
      authors =
        dataset.content.organisations
          ?.filter((o) => o.roles.includes('author'))
          .map((o) => o.name) || [];
    }

    if (authors.length < 2) {
      return authors.at(0) || '';
    }

    const lim = 20;
    const separator = authors.length > lim ? '...' : '&';
    const last = authors.at(-1);

    const end = authors.length > lim ? lim - 1 : authors.length - 1;
    const result = `${authors.slice(0, end).join(', ')}, ${separator} ${last}`;

    return result;
  }
}
